<template>
  <div id="situation-center-map" class="si-center-map">
    <sc-map-popup :settings="popupSettings" />
    <sc-map-legend v-if="isMatrixLegendActive" />
  </div>
</template>

<script>
import extent from "turf-extent";
import { initMap } from "./core/";
import { jsonToGeojson } from "@/utils";
import {
  scLayersConfig,
  getFirstSymbolId,
  preparedMapData,
  updateLayerStyle,
  findObjectGeom
} from "./configs/";
import { getTrackFeatures } from "./helpers/";
import debounce from "lodash.debounce";
import iconBus from "@/assets/images/r-icons/si-centre/bus.png";
import iconBusGreen from "@/assets/images/r-icons/si-centre/bus-green.png";
import iconBusBlue from "@/assets/images/r-icons/si-centre/bus-blue.png";
import iconBusError from "@/assets/images/r-icons/si-centre/bus-error.png";
import iconBusActive from "@/assets/images/r-icons/si-centre/bus-active.png";
import iconCamera from "@/assets/images/r-icons/si-centre/camera.png";
import iconCameraActive from "@/assets/images/r-icons/si-centre/camera-active.png";
import iconCameraError from "@/assets/images/r-icons/si-centre/camera-error.png";
import iconCameraWork from "@/assets/images/r-icons/si-centre/camera-work.png";
import iconCameraInactive from "@/assets/images/r-icons/si-centre/camera-inactive.png";
import iconDetectors from "@/assets/images/r-icons/si-centre/detectors.png";
import iconIkDetectors from "@/assets/images/r-icons/si-centre/ik-detectors.png";
import iconTrafficLights from "@/assets/images/r-icons/si-centre/traffic-lights.png";
import iconTrafficLightsActive from "@/assets/images/r-icons/si-centre/traffic-lights-active.png";
import iconTrafficLightsError from "@/assets/images/r-icons/si-centre/traffic-lights-error.png";
import iconTrafficLightsWork from "@/assets/images/r-icons/si-centre/traffic-lights-work.png";
import iconStreetLightActive from "@/assets/images/r-icons/si-centre/street-light-active.png";
import iconStreetLightError from "@/assets/images/r-icons/si-centre/street-light-error.png";
import iconStreetLightWork from "@/assets/images/r-icons/si-centre/street-light-work.png";
import iconMeteostation from "@/assets/images/r-icons/si-centre/meteostation.png";
import iconMeteostationActive from "@/assets/images/r-icons/si-centre/meteostation-active.png";
import iconMeteostationError from "@/assets/images/r-icons/si-centre/meteostation-error.png";
import iconMeteostationWork from "@/assets/images/r-icons/si-centre/meteostation-work.png";
import iconRoadWorks from "@/assets/images/r-icons/si-centre/traffic-sign-1.png";
import iconEvents from "@/assets/images/r-icons/si-centre/traffic-sign-2.png";
import iconAccident from "@/assets/images/r-icons/si-centre/traffic-sign-3.png";
import iconTram from "@/assets/images/r-icons/si-centre/tram.png";
import iconTramActive from "@/assets/images/r-icons/si-centre/tram-active.png";
import iconTrolleybus from "@/assets/images/r-icons/si-centre/trolleybus.png";
import iconTrolleybusActive from "@/assets/images/r-icons/si-centre/trolleybus-active.png";
import iconTlt7 from "@/assets/images/r-icons/si-centre/tlt7.png";
import iconTlt7Active from "@/assets/images/r-icons/si-centre/tlt7-active.png";
import iconBulldozer from "@/assets/images/r-icons/si-centre/bulldozer-2.png";
import iconBulldozerActive from "@/assets/images/r-icons/si-centre/bulldozer-2-active.png";
import iconBulldozerRed from "@/assets/images/r-icons/si-centre/bulldozer-2-red.png";
import iconBulldozerBlue from "@/assets/images/r-icons/si-centre/bulldozer-2-blue.png";
import iconBulldozerGreen from "@/assets/images/r-icons/si-centre/bulldozer-2-green.png";
import iconBulldozerBrown from "@/assets/images/r-icons/si-centre/bulldozer-2-brown.png";
import iconMtaxi from "@/assets/images/r-icons/si-centre/mtaxi.png";
import iconMtaxiActive from "@/assets/images/r-icons/si-centre/mtaxi-active.png";
import iconTrafficConeRed from "@/assets/images/r-icons/si-centre/traffic-cone-red.png";
import iconTrafficConeActive from "@/assets/images/r-icons/si-centre/traffic-cone-active.png";
import iconRoadWork from "@/assets/images/r-icons/si-centre/road-work.png";
import iconRoadWorkActive from "@/assets/images/r-icons/si-centre/road-work-active.png";
import iconRoadEvent from "@/assets/images/r-icons/si-centre/road-event.png";
import iconRoadEventActive from "@/assets/images/r-icons/si-centre/road-event-active.png";
import iconRoadAccident from "@/assets/images/r-icons/si-centre/road-accident.png";
import iconRoadAccidentActive from "@/assets/images/r-icons/si-centre/road-accident-active.png";
import iconCar from "@/assets/images/r-icons/si-centre/car.png";
import iconCarActive from "@/assets/images/r-icons/si-centre/car-active.png";

import ScMapPopup from "./components/sc-map-popup/sc-map-popup";
import ScMapLegend from "./components/sc-map-legend/sc-map-legend";

export default {
  components: {
    ScMapPopup,
    ScMapLegend
  },
  data() {
    return {
      popupSettings: {
        display: "none",
        layer_id: "",
        top: 0,
        left: 0,
        icons: null,
        colors: null,
        values: []
      },
      prevActiveCard: {
        id: "",
        sourcename: ""
      },
      eventAvaliableLayers: [],
      isReady: false,
      scLayersConfig,
      iconBus,
      iconBusGreen,
      iconBusBlue,
      iconBusError,
      iconBusActive,
      iconCamera,
      iconCameraActive,
      iconCameraError,
      iconCameraWork,
      iconCameraInactive,
      iconDetectors,
      iconIkDetectors,
      iconTrafficLights,
      iconTrafficLightsError,
      iconTrafficLightsWork,
      iconTrafficLightsActive,
      iconStreetLightActive,
      iconStreetLightWork,
      iconStreetLightError,
      iconMeteostation,
      iconMeteostationError,
      iconMeteostationWork,
      iconMeteostationActive,
      iconRoadWorks,
      iconRoadWork,
      iconRoadWorkActive,
      iconRoadEvent,
      iconRoadEventActive,
      iconRoadAccident,
      iconRoadAccidentActive,
      iconEvents,
      iconAccident,
      iconTram,
      iconTramActive,
      iconTrolleybus,
      iconTrolleybusActive,
      iconTlt7,
      iconTlt7Active,
      iconBulldozer,
      iconBulldozerActive,
      iconBulldozerRed,
      iconBulldozerBlue,
      iconBulldozerGreen,
      iconBulldozerBrown,
      iconMtaxi,
      iconMtaxiActive,
      iconTrafficConeRed,
      iconTrafficConeActive,
      iconCar,
      iconCarActive
    };
  },
  watch: {
    theme() {
      initMap(this);
      if (this.isZonesActive) {
        this.MatrixController.removeZones();
        this.MatrixController.addZones();
      }
      if (this.isTagilMatrixActive) {
        this.toggleTagilMatrix();
      }
    },
    disabledLayers() {
      this.updateActiveLayers();
      this.updateActiveTileLayers();
      this.updatePopupState();
    },
    layersItems() {
      if (!this.isReady) return;
      this.updateActiveLayers();
    },
    activeCardId(val) {
      this.TrafficLightsController.toggleTurns(val);
      if (this.prevActiveCard.id)
        updateLayerStyle(this, this.prevActiveCard.id, true);
      if (val) updateLayerStyle(this, val);
      this.prevActiveCard.id = val;
      this.prevActiveCard.sourcename = this.activeCardLayerId;
    },
    trackLayer: {
      handler: function() {
        this.updateTrackLayer();
      },
      deep: true
    },
    layersData: {
      handler: function() {
        this.updateData();
      },
      deep: true
    },
    isZonesActive(val) {
      this.toggleZones(val);
    },
    isTagilMatrixActive() {
      this.toggleTagilMatrix();
    },
    isPtRouteActive() {
      this.toggleTagilPtRoute();
    },
    flyToObject: {
      handler: function(val) {
        if (!val) return;
        this.flyTo(findObjectGeom(this, val));
        this.$store.commit("CLEAR_SI_CENTER_FLY_TO");
      },
      deep: true
    }
  },
  computed: {
    token() {
      return this.$store.state.auth.token;
    },
    instance() {
      return this.$store.state.instance;
    },
    theme() {
      return this.$store.state.theme;
    },
    mapConfig() {
      return this.$store.state.situationCenter.config.map;
    },
    layersData() {
      return this.$store.state.situationCenter.layersData.map;
    },
    mapInstanceConfig() {
      return this.$store.state.situationCenter.config
        ? this.$store.state.situationCenter.config.mapLayers
        : [];
    },
    activeCardId() {
      return this.$store.state.situationCenter.objectCard.data
        ? this.$store.state.situationCenter.objectCard.data.properties.id
        : null;
    },
    activeCardLayerId() {
      return this.$store.state.situationCenter.objectCard
        ? this.$store.state.situationCenter.objectCard.layer_id
        : null;
    },
    layersItems() {
      return Object.keys(this.layersData);
    },
    tileLayersItems() {
      return this.$store.state.situationCenter.config.mapTileLayers
        ? this.$store.state.situationCenter.config.mapTileLayers.map(e => e.id)
        : [];
    },
    disabledLayers() {
      return this.$store.state.situationCenter.disabledLayers || [];
    },
    tileLayers() {
      return this.$store.state.situationCenter.config.mapTileLayers || [];
    },
    trackLayer() {
      return this.$store.state.situationCenter.trackLayer;
    },
    activeSC() {
      return this.$store.state.situationCenter.activeSC || null;
    },
    isZonesActive() {
      return this.$store.state.situationCenter.matrix.enabled;
    },
    isMatrixLegendActive() {
      const { matrix } = this.$store.state.situationCenter;
      const { enabled, active } = matrix;

      return !!enabled && !!active;
    },
    tagilMatrixData() {
      return this.$store.state.situationCenter.tagilMatrix.data;
    },
    isTagilMatrixActive() {
      const { enabled, data } = this.$store.state.situationCenter.tagilMatrix;

      return enabled && !!data;
    },
    flyToObject() {
      return this.$store.state.situationCenter.flyTo || null;
    },
    isPtRouteActive() {
      return !this.disabledLayers.find(e => e === "ptRoute");
    }
  },
  methods: {
    targetClickHandler(info, id, layer_id) {
      this.$store.commit("SET_SI_CENTER_OBJECT_CARD", {
        data: info,
        layer_id,
        id
      });
    },
    updateData: debounce(function() {
      if (!this.layersItems) return;
      this.layersItems.forEach(item => {
        if (this.disabledLayers.indexOf(item) === -1) {
          const sourcename = `${item}_sc_map`;
          const features = this.layersData[item];
          const featuresByTypes = {};

          features.forEach(e => {
            if (e.geom && e.geom.type) {
              if (e.geom.type === "Point") {
                e.geometry_clone = e.geom.coordinates;
              }
              if (!featuresByTypes[e.geom.type]) {
                featuresByTypes[e.geom.type] = [];
              }
              featuresByTypes[e.geom.type].push(e);
            }
          });

          if (Object.keys(featuresByTypes).length) {
            Object.keys(featuresByTypes).forEach(e => {
              const source = this.mapgl.getSource(`${sourcename}_${e}`);
              const sourceSymbol = this.mapgl.getSource(`${sourcename}_${e}_s`);
              const data = jsonToGeojson(
                preparedMapData(item, featuresByTypes[e])
              );

              if (!this.layersData[item]) return;
              if (source) source.setData(data);
              if (sourceSymbol) sourceSymbol.setData(data);
            });
          } else {
            if (
              this.mapInstanceConfig.find(layer => layer.type === "filterable")
            ) {
              const filterableLayers = this.mapInstanceConfig.filter(
                layer => layer.type === "filterable"
              );
              filterableLayers.forEach(fl => {
                const filteredSources = Object.keys(
                  this.scLayersConfig
                ).filter(str => str.includes(fl.layer_id));
                filteredSources.forEach(src => {
                  const layerType = src.slice(fl.layer_id.length);
                  const source = this.mapgl.getSource(sourcename + layerType);
                  if (source)
                    source.setData({
                      type: "FeatureCollection",
                      features: []
                    });
                });
              });
            }
          }
        }
      });
      if (this.$store.state.situationCenter.filterConfig.isLoading) {
        this.$store.commit("SET_SI_CENTER_FILTER_CONFIG", {
          field: "isLoading",
          value: false
        });
      }
    }, 2000),
    beforeToggleLayers(layer_id) {
      const sourcename = `${layer_id}_sc_map`;
      const features = this.layersData[layer_id];
      if (!features) return;
      const featuresByTypes = {};
      features.forEach(e => {
        if (e.geom && e.geom.type) {
          if (!featuresByTypes[e.geom.type]) featuresByTypes[e.geom.type] = [];
          featuresByTypes[e.geom.type].push(e);
        }
      });

      Object.keys(featuresByTypes).forEach(e => {
        const source = this.mapgl.getSource(`${sourcename}_${e}`);
        if (source && this.disabledLayers.indexOf(layer_id) > -1)
          this.removeMapLayer(`${sourcename}_${e}`, e);
        else if (!source && this.disabledLayers.indexOf(layer_id) === -1)
          this.addMapLayer(
            layer_id,
            `${sourcename}_${e}`,
            e,
            featuresByTypes[e]
          );
      });
    },
    removeMapLayer(sourcename, type) {
      if (this.mapgl.getLayer(sourcename)) {
        this.mapgl.removeLayer(sourcename);
        if (type === "Point") this.mapgl.removeLayer(`${sourcename}_s`);
      }
      this.mapgl.removeSource(sourcename);
      if (type === "Point") this.mapgl.removeSource(`${sourcename}_s`);
    },
    addMapLayer(layer_id, sourcename, type, features) {
      if (!features) return;

      const data = jsonToGeojson(preparedMapData(layer_id, features));

      this.mapgl.addSource(sourcename, {
        type: "geojson",
        data
      });
      if (
        layer_id === "12ff4f13-bb98-43c8-bd34-d69808b72c5d" ||
        layer_id === "7fc1a76e-84e7-44b7-aed0-b3df8b04b867"
      ) {
        this.mapgl.addLayer(
          {
            id: sourcename,
            source: sourcename,
            ...this.scLayersConfig[`${layer_id}_${type}`]
          },
          getFirstSymbolId(this.mapgl)
        );
      } else {
        this.mapgl.addLayer({
          id: sourcename,
          source: sourcename,
          ...this.scLayersConfig[`${layer_id}_${type}`]
        });
      }

      if (type === "Point") {
        this.mapgl.addSource(`${sourcename}_s`, {
          type: "geojson",
          data
        });
        this.mapgl.addLayer({
          id: `${sourcename}_s`,
          source: `${sourcename}_s`,
          ...this.scLayersConfig[`${layer_id}_${type}_s`]
        });
      }
      this.updateEventsLayers(sourcename);
    },
    toggleTileLayer(layer_id, value) {
      this.mapgl.setLayoutProperty(layer_id, "visibility", value);
    },
    updateActiveLayers() {
      if (!this.layersItems) return;
      this.layersItems.forEach(item => this.beforeToggleLayers(item));
      if (this.activeCardId) {
        this.TrafficLightsController.toggleTurns(this.activeCardId);
        if (this.prevActiveCard.id)
          updateLayerStyle(this, this.prevActiveCard.id, true);
        if (this.activeCardId) updateLayerStyle(this, this.activeCardId);
        this.prevActiveCard.id = this.activeCardId;
        this.prevActiveCard.sourcename = this.activeCardLayerId;
      }
    },
    updateActiveTileLayers() {
      if (!this.tileLayersItems) return;
      this.tileLayersItems.forEach(layer_id => {
        const currentProp = this.mapgl.getLayoutProperty(
          layer_id,
          "visibility"
        );
        if (
          currentProp === "visible" &&
          this.disabledLayers.indexOf(layer_id) > -1
        ) {
          this.mapgl.setLayoutProperty(layer_id, "visibility", "none");
        } else if (
          currentProp === "none" &&
          this.disabledLayers.indexOf(layer_id) === -1
        ) {
          this.mapgl.setLayoutProperty(layer_id, "visibility", "visible");
        }
      });
    },
    updatePopupState() {
      if (this.disabledLayers.indexOf(this.popupSettings.layer_id) > -1) {
        this.popupSettings.display = "none";
        this.popupSettings.values = [];
        this.popupSettings.layer_id = "";
      }
    },
    updateEventsLayers(sourcename) {
      if (this.eventAvaliableLayers.indexOf(sourcename) === -1)
        this.eventAvaliableLayers.push(sourcename);
    },
    updateTrackLayer() {
      const layer_id = "track_layer";
      const sourcename = `${layer_id}_sc_map`;
      const source = this.mapgl.getSource(sourcename);
      if (!this.trackLayer.layer_id) {
        if (source) {
          if (this.mapgl.getLayer(sourcename)) {
            this.mapgl.removeLayer(sourcename);
          }
          this.mapgl.removeSource(sourcename);
        }
      } else {
        const features = getTrackFeatures(this.trackLayer);
        if (!features) return;

        const data = jsonToGeojson(features);
        if (source) {
          source.setData(data);
        } else {
          this.mapgl.addSource(sourcename, {
            type: "geojson",
            data
          });
          if (
            this.trackLayer.layer_id !== "TagilPublicTransport?format=vehicles"
          ) {
            this.mapgl.addLayer({
              id: sourcename,
              source: sourcename,
              ...this.scLayersConfig[layer_id]
            });
          } else {
            this.mapgl.addLayer({
              id: sourcename,
              source: sourcename,
              ...this.scLayersConfig.track_layer_tagil
            });
          }
        }
      }
    },
    toggleZones(val) {
      if (val) this.MatrixController.addZones();
      else this.MatrixController.removeZones();
    },
    toggleTagilMatrix() {
      this.TagilMatrixController.toggleItems();
    },
    toggleTagilPtFilterable() {
      this.TagilPtFilterableController.toggleItems();
    },
    toggleTagilPtRoute() {
      this.TagilPtRouteController.toggleItems();
    },
    flyTo(geom) {
      if (!geom) return false;
      if (geom.type === "Point") {
        this.mapgl.flyTo({
          center: geom.coordinates,
          zoom: 14
        });
      } else {
        const bounds = extent(geom);
        this.mapgl.fitBounds(bounds, {
          padding: 40
        });
      }
    }
  },
  mounted() {
    initMap(this);
  }
};
</script>

<style lang="scss">
.si-center-map {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  .mapboxgl-canvas {
    &:focus {
      outline: none;
    }
  }
}
</style>
